import { useState } from 'react';
import parse from 'html-react-parser';
import getIconSize from '~/helpers/SVGIcons/getIconSize';
import { SVGProps, TooltipPosition } from '~/helpers/SVGIcons/types';
import getIconSVG from '~/helpers/SVGIcons/getIconSVG';

type TooltipProps = {
  id?: string | undefined;
  visible: boolean;
  position: TooltipPosition;
};

const IconWrapper: React.FC<Omit<SVGProps, 'width' | 'height'>> = ({
  name,
  color,
  rect,
  size = 'medium',
  strokeColor,
  strokeWidth,
  className,
  tooltip,
  tooltipToBold,
  tooltipPosition = 'bottom-right',
  theme = 'dark',
  ...props
}) => {
  const { width, height } = getIconSize(size) as SVGProps;
  const svgData = getIconSVG(name) as string[];
  const [showTooltip, setShowTooltip] = useState<TooltipProps>({
    id: '',
    visible: false,
    position: tooltipPosition,
  });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const iconRightEdge = e.clientX + e.currentTarget.clientWidth;
    const screenRightEdge = window.innerWidth;
    const rightWithoutSpace = iconRightEdge + 100 > screenRightEdge;
    const position = rightWithoutSpace
      ? tooltipPosition === 'bottom-right'
        ? 'bottom-left'
        : tooltipPosition === 'top-right'
          ? 'top-left'
          : tooltipPosition
      : tooltipPosition;

    setShowTooltip({
      id: tooltip?.toString(),
      visible: true,
      position: position,
    });
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() =>
        setShowTooltip({ id: '', visible: false, position: tooltipPosition })
      }
    >
      <svg
        width={width}
        height={height}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        {...props}
      >
        {rect && (
          <rect
            x="4.4"
            y="4.4"
            width="15.2"
            height="15.2"
            rx="1.1"
            stroke="white"
            strokeWidth="0.8"
          />
        )}
        {svgData?.map((item: string, _i: number) => (
          <path
            key={item}
            d={item}
            fill={color}
            stroke={strokeWidth ? strokeColor : undefined}
            strokeWidth={strokeWidth ? strokeWidth : 0}
          />
        ))}
      </svg>
      {showTooltip.visible && tooltip !== undefined && (
        <span
          className={`shadow-around absolute z-20 whitespace-nowrap rounded-md px-2 py-1 text-start text-xs 
          ${
            theme === 'light'
              ? 'bg-white text-black'
              : 'bg-neutra3-cinzaquente10 text-white'
          } 
          ${
            showTooltip.position === 'bottom-left'
              ? '-bottom-full right-0'
              : showTooltip.position === 'top-left'
                ? '-top-full right-0'
                : showTooltip.position === 'top-right'
                  ? '-top-full left-0'
                  : showTooltip.position === 'middle-right'
                    ? 'left-full top-1/2 ml-2 -translate-y-1/2'
                    : '-bottom-full left-0'
          }`}
        >
          {tooltipToBold
            ? parse(
                tooltipToBold
                  .map(word => tooltip.replace(word, `<b>${word}</b>`))
                  .toString(),
              )
            : tooltip}
        </span>
      )}
    </div>
  );
};

export { IconWrapper as SVGIcon };
